import * as React from 'react'
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog'
import { cn } from '@/lib/utils'

/**
 * Figma (node 112:601 — Dialog used as Alert Dialog):
 * Overlay: fixed inset-0, bg-black/80
 * Content: bg-background, border border-border, rounded-xl (14px), max-w-[425px],
 *   overflow-clip, flex flex-col items-end
 * Header: flex flex-col gap-2, p-4, text-left
 *   Title: text-base font-medium leading-none text-foreground
 *   Description: text-sm font-normal leading-5 text-muted-foreground
 * Content slot: px-4 pb-4
 * Footer: border-t border-border, bg-muted/50, flex gap-2 items-center justify-end, p-4
 *   Cancel: outline button (h-8, rounded-md, border-input)
 *   Action: primary button (h-8, rounded-md, bg-primary)
 * Non-dismissible: no backdrop click, requires explicit action
 */

const IntuiAlertDialog = AlertDialogPrimitive.Root

const IntuiAlertDialogTrigger = AlertDialogPrimitive.Trigger

const IntuiAlertDialogOverlay = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-[999999] bg-black/80',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
))
IntuiAlertDialogOverlay.displayName = 'IntuiAlertDialogOverlay'

const IntuiAlertDialogContent = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Portal>
    <IntuiAlertDialogOverlay />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-1/2 top-1/2 z-[999999] -translate-x-1/2 -translate-y-1/2',
        'w-full max-w-[425px] overflow-clip rounded-xl border border-border bg-background',
        'flex flex-col shadow-lg',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
        'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        className
      )}
      {...props}
    />
  </AlertDialogPrimitive.Portal>
))
IntuiAlertDialogContent.displayName = 'IntuiAlertDialogContent'

const IntuiAlertDialogHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col gap-2 p-4 text-left', className)}
    {...props}
  />
))
IntuiAlertDialogHeader.displayName = 'IntuiAlertDialogHeader'

const IntuiAlertDialogTitle = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn('text-base font-medium leading-none text-foreground', className)}
    {...props}
  />
))
IntuiAlertDialogTitle.displayName = 'IntuiAlertDialogTitle'

const IntuiAlertDialogDescription = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn('text-sm font-normal leading-5 text-muted-foreground', className)}
    {...props}
  />
))
IntuiAlertDialogDescription.displayName = 'IntuiAlertDialogDescription'

const IntuiAlertDialogFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex gap-2 items-center justify-end p-4 border-t border-border bg-muted/50',
      className
    )}
    {...props}
  />
))
IntuiAlertDialogFooter.displayName = 'IntuiAlertDialogFooter'

const IntuiAlertDialogAction = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action> & {
    /** Figma: "Destructive: No" = default (black), "Destructive: Yes" = destructive (light red bg, red text) */
    variant?: 'default' | 'destructive'
  }
>(({ className, variant = 'default', ...props }, ref) => (
  <AlertDialogPrimitive.Action
    ref={ref}
    className={cn(
      'inline-flex items-center justify-center h-8 px-2.5 rounded-md text-sm font-medium',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      variant === 'destructive'
        ? 'bg-destructive/10 text-destructive hover:bg-destructive/20'
        : 'bg-primary text-primary-foreground hover:bg-primary/90',
      className
    )}
    {...props}
  />
))
IntuiAlertDialogAction.displayName = 'IntuiAlertDialogAction'

const IntuiAlertDialogCancel = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={cn(
      'inline-flex items-center justify-center h-8 px-2.5 rounded-md text-sm font-medium',
      'border border-input bg-background text-foreground hover:bg-accent',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
))
IntuiAlertDialogCancel.displayName = 'IntuiAlertDialogCancel'

export {
  IntuiAlertDialog,
  IntuiAlertDialogTrigger,
  IntuiAlertDialogContent,
  IntuiAlertDialogHeader,
  IntuiAlertDialogTitle,
  IntuiAlertDialogDescription,
  IntuiAlertDialogFooter,
  IntuiAlertDialogAction,
  IntuiAlertDialogCancel,
}
